import { useEffect, useContext, useState } from 'react';
import axios from 'axios';
import { Box, IconButton, Snackbar, Alert } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { LanguageContext } from '../../../../../context/languageContext';
import { DataGridPremium, useGridApiRef } from '@mui/x-data-grid-premium';
import EditSkillingSheet from './editRetailerSkills';

export default function RetailerSkillingSheet({user}) {
  const [data, setData] = useState();
  const [isLoading, setIsLoading] = useState(true);
  const [isEditSkillingSheetModalOpen, setisEditSkillingSheetModalOpen] =
    useState(false);
  const [currentEntry, setCurrentEntry] = useState(null);
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setIsSnackbarOpen(false);
  };
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const { translations } = useContext(LanguageContext);
  const apiRef = useGridApiRef();

  const skillingSheetReport = translations => ({
    apiEndpoint: `${process.env.REACT_APP_ENDPOINT_ADMIN}/workforce/skilling-sheet/retailer/get`,
    columns: [
      {
        field: 'name',
        headerName: 'Name',
      },
      {
        field: 'location',
        headerName: 'Location',
      },
      {
        field: 'ua',
        headerName: 'Universal Agent',
      },
      {
        field: 'spa',
        headerName: 'Spanish',
      },
      {
        field: 'disp',
        headerName: 'Dispatch',
      },
      {
        field: 'keys',
        headerName: 'Keys',
      },
      {
        field: 'chats',
        headerName: 'Chats',
      },
      {
        field: 'emails',
        headerName: 'Emails',
      },
      {
        field: 'ca',
        headerName: 'California',
      },
      {
        field: 'co',
        headerName: 'Colorado',
      },
      {
        field: 'ct',
        headerName: 'Connecticut',
      },
      {
        field: 'fl',
        headerName: 'Florida',
      },
      {
        field: 'ga',
        headerName: 'Georgia',
      },
      {
        field: 'in',
        headerName: 'Indiana',
      },
      {
        field: 'ky',
        headerName: 'Kentucky',
      },
      {
        field: 'ant',
        headerName: 'Antilles',
      },
      {
        field: 'mi',
        headerName: 'Michigan',
      },
      {
        field: 'mn',
        headerName: 'Minnesota',
      },
      {
        field: 'mo',
        headerName: 'Missouri',
      },
      {
        field: 'ms',
        headerName: 'Mississippi',
      },
      {
        field: 'nc',
        headerName: 'North Carolina',
      },
      {
        field: 'ne',
        headerName: 'Nebraska',
      },
      {
        field: 'nj',
        headerName: 'New Jersey',
      },
      {
        field: 'ny',
        headerName: 'New York',
      },
      {
        field: 'ri',
        headerName: 'Rhode Island',
      },
      {
        field: 'sc',
        headerName: 'South Carolina',
      },
      {
        field: 'sd',
        headerName: 'South Dakota',
      },
      {
        field: 'tx',
        headerName: 'Texas',
      },
      {
        field: 'wa',
        headerName: 'Washington',
      },
      {
        field: 'wi',
        headerName: 'Wisconsin',
      },
      {
        field: 'wv',
        headerName: 'West Virginia',
      },
      {
        field: 'tn',
        headerName: 'Tennessee',
      },
      {
        field: 'va',
        headerName: 'Virginia',
      },
      {
        field: 'totalUA',
        headerName: 'Total',
      },
      {
        field: 'edit',
        headerName: 'Edit',
        renderCell: params => {
          return (
            <IconButton
              onClick={() => handleEditSkillingSheetModalOpenClose(params.row)}
            >
              <EditIcon />
            </IconButton>
          );
        },
      },
    ],
  });

  const skillingSheet = skillingSheetReport(translations);

  const handleEditSkillingSheetModalOpenClose = entry => {
    setCurrentEntry(isEditSkillingSheetModalOpen ? null : entry);
    setisEditSkillingSheetModalOpen(!isEditSkillingSheetModalOpen);
  };

  useEffect(() => {
    const fetchData = async () => {
      const response = await axios.get(skillingSheet.apiEndpoint);
      setData({
        rows: response.data,
      });
    };
    fetchData();
  }, [skillingSheet.apiEndpoint]);

  useEffect(() => {
    if (!data) return;

    const timeoutId = setTimeout(() => {
      apiRef.current.autosizeColumns({
        expand: true,
        includeHeaders: true,
        includeOutliers: true,
      });
      setIsLoading(false);
    });

    return () => clearTimeout(timeoutId);
  }, [apiRef, data]);

  const handleRefresh = async () => {
    setIsLoading(true);
    const response = await axios.get(skillingSheet.apiEndpoint);
    setData({
      rows: response.data,
    });
  };

  return (
    <Box
      sx={{
        height: '90vh',
        width: '100%',
        display: 'flex',
        p: 2,
      }}
    >
      <DataGridPremium
        {...data}
        loading={isLoading}
        columns={skillingSheet.columns}
        disableRowSelectionOnClick
        showToolbar
        sx={{ fontFamily: 'BrightstarSans-Regular' }}
        apiRef={apiRef}
        initialState={{ pinnedColumns: { left: ['name'], right: ['edit'] } }}
      />
      {isEditSkillingSheetModalOpen && (
        <EditSkillingSheet
          handleRefresh={handleRefresh}
          isOpen={isEditSkillingSheetModalOpen}
          handleClose={handleEditSkillingSheetModalOpenClose}
          entry={currentEntry}
          setIsSnackbarOpen={setIsSnackbarOpen}
          setSnackbarMessage={setSnackbarMessage}
          setSnackbarSeverity={setSnackbarSeverity}
          user={user}
        />
      )}
      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbarSeverity}
          variant='filled'
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
